/* eslint-disable no-restricted-imports */
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { browserHistory } from 'react-router';

import { datadogLogs, Logger } from '@datadog/browser-logs';
import translationMessages from 'i18n/translationMessages';
import { themeOptionsBase } from 'TODO/ui-core';

import {
  EModule,
  EThemeKey,
  getNavigateTo,
  getThemeOptions,
  UIModulesProvider,
  useIntlMessages,
  useSocketEventsHandler,
} from '@lumahealthhq/ui-common';
import { CssBaseline } from '@lumahealthhq/ui-core';
import { useCurrentEnv, useSetCurrentAuth, useSetSocket, useSocket } from '@lumahealthhq/ui-model';

import { useCurrentLocale, useCurrentMaster, useCurrentUser } from '@lib/hooks';
import { RolloutEnum } from '@lib/resources/types';
import configService from '@lib/services/configService';
import { KEYS } from '@lib/services/storageService';
import axiosClient from '@lib/utils/axiosClient';

import { changeLocale } from 'actions';
import { snackbarError, snackbarSuccess } from 'actions/snackbarMessage';
import { useModulesProviderConfig, useModulesProviderRoutingMap } from 'hooks';
import { getSocket } from 'utils/socket';

import { switchAccount } from './containers/Header/actions';

const keyEnv = 'web-app:env';
const env = configService.getPodName();

let DDLogs: null | Logger = null;

// TODO - @rrubenich move logs logic to ui-core to be reusable
// Initialize Datadog Logs
if (process.env.REACT_APP_POD_NAME === 'production') {
  datadogLogs.init({
    clientToken: configService.getDatadogClientToken() || '',
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: 'web-app',
    env: process.env.REACT_APP_POD_NAME,
    version: configService.getCommitHash(),
  });

  DDLogs = datadogLogs.createLogger('web-app', {
    handler: 'http',
    context: {
      environment: process.env.NODE_ENV,
      service: 'web-app',
    },
  });
}

enum LoggerStatusEnum {
  debug = 'debug',
  info = 'info',
  warn = 'warn',
  error = 'error',
}

type TMeta = Record<string, any>;
type TMessage = string | Record<string, any>;

const logger = {
  log: (message: TMessage, level = LoggerStatusEnum.info, meta: TMeta) => {
    const formattedMessage = typeof message === 'string' ? message : JSON.stringify(message);
    const formattedMeta = meta ? JSON.stringify(meta) : '';
    const logMessage = `${level}: ${formattedMessage} ${formattedMeta}`;

    // Added to also show the log on dev tools
    // eslint-disable-next-line no-console
    console[level](logMessage);

    if (DDLogs != null && level !== LoggerStatusEnum.debug) {
      DDLogs.log(logMessage, { level });
    }
  },
  error: (message: TMessage, meta: TMeta) => logger.log(message, LoggerStatusEnum.error, meta),
  warn: (message: TMessage, meta: TMeta) => logger.log(message, LoggerStatusEnum.warn, meta),
  info: (message: TMessage, meta: TMeta) => logger.log(message, LoggerStatusEnum.info, meta),
  debug: (message: TMessage, meta: TMeta) => logger.log(message, LoggerStatusEnum.debug, meta),
};

function SocketHandler() {
  // Handle queries invalidation on socket events for federated modules
  useSocketEventsHandler();
  return null;
}

function AuthHandler() {
  const setAuth = useSetCurrentAuth();
  const currentUser = useCurrentUser();
  const currentMaster = useCurrentMaster();
  const [, setEnv] = useCurrentEnv();

  // Reuse the web-app auth for federated modules
  useEffect(() => {
    setAuth({ currentMaster, currentUser } as any);

    // WORKAROUND force the env as ui-packages still uses the localStorage
    // instead of the context's value
    // We're using the same effect than auth to prevent racing conditions
    setEnv(env as any);
  }, [currentMaster, currentUser, setAuth, setEnv]);

  // Reuse the web-app socket for federated modules
  const socket = useSocket();
  const setSocket = useSetSocket();
  useEffect(() => {
    const newSocket = getSocket();
    // Listening for `currentUser` changes bc we can't listen for `socket` changes
    // (the socket in web-app is created using a saga instead of a hook)
    if (currentUser && !socket) setSocket(newSocket);
  }, [currentUser, socket, setSocket]);

  return socket ? <SocketHandler /> : null;
}
export interface ModulesProviderProps {
  children: React.ReactNode;
}

export default function ModulesProvider({ children }: ModulesProviderProps) {
  const locale = useCurrentLocale() as any;
  const dispatch = useDispatch();
  const intlMessages = useIntlMessages(locale);
  const config = useModulesProviderConfig();
  const routingMap = useModulesProviderRoutingMap();

  const handleSwitchAccount = (organization: string, account: string) => dispatch(switchAccount(organization, account));

  const messages = useMemo(
    () => ({
      // @ts-ignore
      ...translationMessages[locale],
      ...intlMessages,
    }),
    [locale, intlMessages],
  );

  const themeOptions = useMemo(() => getThemeOptions(EThemeKey.Base, themeOptionsBase), []);

  return (
    <UIModulesProvider
      // Router
      routingMap={routingMap}
      productName={{
        [EModule.ContainerTableFollowupWorkflowInstances]: [RolloutEnum.NextyFollowupWorkflows],
        [EModule.ContainerTableFollowupWorkflows]: [RolloutEnum.NextyFollowupWorkflows],
        [EModule.ContainerFollowupWorkflowBuilder]: [RolloutEnum.NextyFollowupWorkflows],
      }}
      // TODO ramon/daniel rename this prop to `onMessage`
      onOpen={({ variant, message }) => {
        const snackbar = variant === 'error' ? snackbarError : snackbarSuccess;
        // TODO ramon/daniel accept only string on "message" prop
        dispatch(snackbar(String(message)));
      }}
      navigate={({ hasAccess, modulePathname, params, debounced }) => {
        if (hasAccess) {
          const to = getNavigateTo(modulePathname, params);
          if (debounced) return to;

          // Ensure we open absolute URLs on a new blank.
          // E.g. for patient-web-app URLs
          if (modulePathname && /^http/i.test(modulePathname)) {
            // Note that we can't use `to.pathname` here bc it has double `//` replaced by `/`
            const url = `${modulePathname}${to?.search || ''}`;
            window.open(url, '_blank');
          } else {
            browserHistory.push(to);
          }
        }
      }}
      navigateBack={() => browserHistory.goBack()}
      // UIComponents
      messages={messages}
      // UICore
      themeOptions={themeOptions}
      // UIModel
      env={env as any}
      keyEnv={keyEnv}
      keyAuthToken={KEYS.user}
      switchAccount={handleSwitchAccount}
      // UIUtils
      logger={logger}
      // @ts-ignore TODO danielmana remove ignore once the IConfig type is adjusted
      config={config}
      locale={locale}
      setLocale={(newLocale) => dispatch(changeLocale(newLocale as any))}
      axiosClient={axiosClient}
    >
      <CssBaseline />
      <AuthHandler />
      {children}
    </UIModulesProvider>
  );
}
